import { CSSProperties, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

const PROJECT_THEMES_KEY = 'projectThemes';

type TGradientLoaderProps = {
  top?: CSSProperties['top'];
  left?: CSSProperties['left'];
  position?: CSSProperties['position'];
  transform?: CSSProperties['transform'];
  height?: CSSProperties['height'];
  width?: CSSProperties['width'];
  borderRadius?: CSSProperties['borderRadius'];
  filter?: CSSProperties['filter'];
  zIndex?: CSSProperties['zIndex'];
  opacity?: number;
};

const GradientLoader = (props?: TGradientLoaderProps) => {
  const {
    top = '-55vh',
    left = '50%',
    position = 'fixed',
    transform = 'translateX(-50%)',
    height = '816px',
    width = '816px',
    borderRadius = '50%',
    filter = 'blur(200px)',
  } = props || {};

  const location = useLocation();
  const { projectUuid } = useParams();

  const { primaryColor, mode } = useMemo<{ primaryColor?: string; mode?: string }>(() => {
    let themeDetails: { primaryColor?: string; mode?: string } | null;

    // check localStorage for the project theme settings
    // these will be as up to day
    if (projectUuid) {
      let locallyStoredThemeDetails: Record<string, { primaryColor?: string; mode?: string }> = {};
      try {
        locallyStoredThemeDetails = JSON.parse(localStorage.getItem(PROJECT_THEMES_KEY) || '{}');
      } catch (error) {
        console.error('Failed to parse project themes from localStorage:', error);
        locallyStoredThemeDetails = {};
      }
      themeDetails = locallyStoredThemeDetails[projectUuid];
      if (themeDetails) return themeDetails;
    }

    themeDetails = location?.state?.projectTheme || null;

    if (themeDetails) {
      return themeDetails;
    }

    // we have not retrieved themeDetails from location.state or from localStorage
    // use the default background color for the channel type. No primary color will show
    const details = { mode: 'dark' };
    try {
      const url = new URL(window.location.href);
      if (url.pathname.startsWith('/webinarplus')) {
        details.mode = 'light';
      }
      return details;
    } catch (e) {
      console.error('URL error: ', e);
      return details;
    }
  }, [location?.state?.projectTheme, projectUuid]);

  const styles: CSSProperties = useMemo(() => {
    return {
      position,
      transform,
      borderRadius,
      animation: 'gradientPulse 2s ease-in-out infinite',
      background: primaryColor || 'none',
      filter,
      top,
      left,
      height,
      width,
      pointerEvents: 'none',
    };
  }, [borderRadius, filter, height, left, position, primaryColor, top, transform, width]);

  return (
    <div
      data-component={'GradientLoader'}
      style={{ height: '100%', width: '100%', margin: 0, backgroundColor: mode === 'light' ? '#f5f7ff' : '#1a1a1f' }}
    >
      <style>{`@keyframes gradientPulse { 0%, 100% { opacity: 0.32; } 50% { opacity: 0.22; } }`}</style>
      <div style={styles} />
    </div>
  );
};

GradientLoader.displayName = 'FullPageProjectThemeGradient';

export default GradientLoader;
